import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/useAuth';

// ─────────────────────────────────────────────────────────────
// Boutique de linge employé à points.
// Solde = somme du grand livre (employee_points_entries) + quarts × 0,10.
// ─────────────────────────────────────────────────────────────

export const POINTS_PER_SHIFT = 0.1;

export type PointsType = 'start' | 'manual' | 'transfer' | 'order' | 'order_refund';

export interface ClothingItem {
  id: string;
  name: string;
  description: string | null;
  point_cost: number;
  photo_path: string | null;
  sizes: string[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PointsEntry {
  id: string;
  employee_id: string;
  delta: number;
  type: PointsType;
  note: string | null;
  order_id: string | null;
  created_at: string;
}

export interface EmployeePoints {
  balance: number;
  startBonus: number;
  earnedShifts: number;
  adjustments: number; // manual + transfer
  spent: number; // order + order_refund (négatif net)
  shiftCount: number;
  entries: PointsEntry[];
}

export interface ClothingOrderItem {
  id: string;
  order_id: string;
  clothing_item_id: string | null;
  item_name: string;
  size: string | null;
  qty: number;
  point_cost_each: number;
}

export type OrderStatus = 'pending' | 'fulfilled' | 'cancelled';

export interface ClothingOrder {
  id: string;
  employee_id: string;
  status: OrderStatus;
  total_cost: number;
  note: string | null;
  created_at: string;
  fulfilled_at: string | null;
  cancelled_at: string | null;
  items: ClothingOrderItem[];
  employee?: { first_name: string; last_name: string | null } | null;
}

/** Format points : « 42,5 pts » (max 2 décimales). */
export function formatPoints(n: number): string {
  return (
    n.toLocaleString('fr-CA', { maximumFractionDigits: 2 }) +
    ' pt' + (Math.abs(n) >= 2 ? 's' : '')
  );
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'En attente',
  fulfilled: 'Remis',
  cancelled: 'Annulé',
};

// ─────────────────────────────────────────────
// Catalogue
// ─────────────────────────────────────────────
const ITEMS_KEY = ['boutique', 'items'] as const;

export function useClothingItems(opts?: { activeOnly?: boolean }) {
  const activeOnly = opts?.activeOnly ?? false;
  return useQuery({
    queryKey: [...ITEMS_KEY, { activeOnly }] as const,
    queryFn: async (): Promise<ClothingItem[]> => {
      let q = supabase
        .from('clothing_items')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (activeOnly) q = q.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ClothingItem[];
    },
    staleTime: 60 * 1000,
  });
}

export type ClothingItemInput = {
  name: string;
  description: string | null;
  point_cost: number;
  photo_path: string | null;
  sizes: string[];
  is_active: boolean;
};

export function useCreateClothingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ClothingItemInput): Promise<void> => {
      const { error } = await supabase.from('clothing_items').insert(payload);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

export function useUpdateClothingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<ClothingItemInput>;
    }): Promise<void> => {
      const { error } = await supabase
        .from('clothing_items')
        .update(payload)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

export function useDeleteClothingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('clothing_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEMS_KEY }),
  });
}

// ─────────────────────────────────────────────
// Points d'un employé
// ─────────────────────────────────────────────
export function useEmployeePoints(employeeId: string | null | undefined) {
  return useQuery({
    queryKey: ['boutique', 'points', employeeId] as const,
    enabled: !!employeeId,
    queryFn: async (): Promise<EmployeePoints> => {
      const [entriesRes, countRes] = await Promise.all([
        supabase
          .from('employee_points_entries')
          .select('*')
          .eq('employee_id', employeeId!)
          .order('created_at', { ascending: false }),
        supabase
          .from('shift_time_entries')
          .select('id', { count: 'exact', head: true })
          .eq('employee_id', employeeId!),
      ]);
      if (entriesRes.error) throw entriesRes.error;
      if (countRes.error) throw countRes.error;

      const entries = (entriesRes.data ?? []) as PointsEntry[];
      const shiftCount = countRes.count ?? 0;
      const earnedShifts = shiftCount * POINTS_PER_SHIFT;

      let startBonus = 0;
      let adjustments = 0;
      let spent = 0;
      let ledger = 0;
      for (const e of entries) {
        ledger += e.delta;
        if (e.type === 'start') startBonus += e.delta;
        else if (e.type === 'manual' || e.type === 'transfer') adjustments += e.delta;
        else spent += e.delta; // order (−) + order_refund (+)
      }

      return {
        balance: ledger + earnedShifts,
        startBonus,
        earnedShifts,
        adjustments,
        spent,
        shiftCount,
        entries,
      };
    },
    staleTime: 30 * 1000,
  });
}

/** Ajuste les points d'un employé (gérant). delta positif ou négatif. */
export function useAdjustPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      employeeId,
      delta,
      type,
      note,
    }: {
      employeeId: string;
      delta: number;
      type: 'manual' | 'transfer';
      note: string | null;
    }): Promise<void> => {
      const { error } = await supabase.from('employee_points_entries').insert({
        employee_id: employeeId,
        delta,
        type,
        note,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['boutique', 'points', v.employeeId] });
    },
  });
}

// ─────────────────────────────────────────────
// Commandes
// ─────────────────────────────────────────────
const ORDER_SELECT =
  '*, items:clothing_order_items(*), employee:employees(first_name, last_name)';

export function useEmployeeOrders(employeeId: string | null | undefined) {
  return useQuery({
    queryKey: ['boutique', 'orders', 'emp', employeeId] as const,
    enabled: !!employeeId,
    queryFn: async (): Promise<ClothingOrder[]> => {
      const { data, error } = await supabase
        .from('clothing_orders')
        .select(ORDER_SELECT)
        .eq('employee_id', employeeId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ClothingOrder[];
    },
    staleTime: 30 * 1000,
  });
}

export function useAllOrders(opts?: { pendingOnly?: boolean }) {
  const pendingOnly = opts?.pendingOnly ?? false;
  return useQuery({
    queryKey: ['boutique', 'orders', 'all', { pendingOnly }] as const,
    queryFn: async (): Promise<ClothingOrder[]> => {
      let q = supabase
        .from('clothing_orders')
        .select(ORDER_SELECT)
        .order('created_at', { ascending: false });
      if (pendingOnly) q = q.eq('status', 'pending');
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ClothingOrder[];
    },
    staleTime: 30 * 1000,
  });
}

/** Passe une commande (employé) — RPC atomique qui vérifie le solde. */
export function usePlaceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      items: { item_id: string; size: string | null; qty: number }[]
    ): Promise<string> => {
      const { data, error } = await supabase.rpc('place_clothing_order', {
        p_items: items,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boutique', 'points'] });
      qc.invalidateQueries({ queryKey: ['boutique', 'orders'] });
    },
  });
}

/** Nombre de commandes « en attente » — pour le badge/notification du menu. */
export const BOUTIQUE_PENDING_KEY = ['boutique', 'pending-count'] as const;

export function usePendingOrdersCount(): number {
  const { isAdminOrOwner } = useAuth();
  const query = useQuery({
    queryKey: BOUTIQUE_PENDING_KEY,
    enabled: isAdminOrOwner,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('clothing_orders')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count ?? 0;
    },
  });
  return query.data ?? 0;
}

/** Change le statut d'une commande (gérant) — RPC (rembourse à l'annulation). */
export function useSetOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      status,
    }: {
      orderId: string;
      status: OrderStatus;
    }): Promise<void> => {
      const { error } = await supabase.rpc('set_clothing_order_status', {
        p_order_id: orderId,
        p_status: status,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boutique', 'orders'] });
      qc.invalidateQueries({ queryKey: ['boutique', 'points'] });
    },
  });
}
